<template>
  <div class="flex flex-col gap-8">
    <!-- Título y subtítulo -->
    <div class="flex flex-col gap-1">
      <titlePrimary :title="isEdit ? $t('property.estateForm.editTitle') : $t('property.estateForm.newTitle')" />
      <p class="text-[#385660] text-sm pt-1">{{ $t('property.estateForm.subtitle') }}</p>
    </div>

    <div v-if="loading" class="text-center p-4">{{ $t('property.detail.loading') }}</div>

    <template v-else>
      <!-- 0. Cuenta cliente. ADS la busca; el cliente solo elige cuando
           pertenece a más de una, que es el caso en el que el sistema no
           puede deducirla por él. -->
      <section v-if="selectableAccounts.length > 1 && !isEdit" class="flex flex-col gap-4 p-6 rounded-xl border border-gray-200 bg-white shadow-[0px_4px_4px_0px_rgba(0,0,0,0.15)]">
        <h2 class="text-[var(--c-primary)] font-semibold">{{ $t('property.estateForm.sections.account') }}</h2>
        <SelectPrimary
          :options="ownAccountOptions"
          :modelValue="selectedAccount?.id ?? ''"
          @update:modelValue="selectOwnAccount"
          :label="$t('property.estateForm.fields.ownAccount')"
          :error="validationErrors.propietario_id"
          class="lg:w-1/2"
        />
      </section>

      <section v-if="isAdsUser && !isEdit" class="flex flex-col gap-4 p-6 rounded-xl border border-gray-200 bg-white shadow-[0px_4px_4px_0px_rgba(0,0,0,0.15)]">
        <h2 class="text-[var(--c-primary)] font-semibold">{{ $t('property.estateForm.sections.account') }}</h2>
        <div class="grid lg:grid-cols-2 grid-cols-1 gap-6">
          <div class="relative">
            <inputPrimary
              v-model="accountSearch"
              :label="$t('property.estateForm.fields.account')"
              :placeholder="$t('property.estateForm.fields.accountPlaceholder')"
              :error="validationErrors.propietario_id"
            />
            <div v-if="accountResults.length" class="absolute left-0 right-0 top-full mt-1 border rounded p-2 bg-white z-20 max-h-60 overflow-auto shadow">
              <ul>
                <li v-for="account in accountResults" :key="account.id" class="hover:bg-gray-100 cursor-pointer px-2 py-1" @click="selectAccount(account)">
                  <p class="text-[#385660]">{{ account.name }}</p>
                  <p class="text-xs text-gray-500">{{ accountTypeLabel(account, '') }}<span v-if="account.email"> · {{ account.email }}</span></p>
                </li>
              </ul>
            </div>
          </div>
          <div v-if="selectedAccount" class="text-sm text-[#385660] self-end pb-3">
            {{ $t('property.estateForm.fields.selectedAccount') }}: <strong>{{ selectedAccount.name }}</strong>
          </div>
        </div>
      </section>

      <!-- Bloques "Suministro 1..3" (16/09/2026): cada uno con los datos del
           inmueble (dirección, CP, municipio, provincia) y del suministro (CUPS
           de luz y gas, tarifas y potencias). Los bloques 2 y 3 se abren con
           "Añadir otro suministro". En edición hay un solo bloque, sin CUPS: los
           suministros se gestionan desde la ficha (ADS-0124). -->
      <section v-for="(block, index) in supplyBlocks" :key="block.uid" :id="`supply-block-${index + 1}`" :data-testid="`supply-block-${index + 1}`"
        class="flex flex-col gap-4 p-6 rounded-xl border border-gray-200 bg-white shadow-[0px_4px_4px_0px_rgba(0,0,0,0.15)]">
        <div v-if="!isEdit" class="flex items-center justify-between gap-4">
          <span class="inline-flex items-center px-4 py-1 rounded-lg bg-[#25B0D7] text-white text-sm font-semibold uppercase shadow-[0px_4px_4px_0px_rgba(0,0,0,0.15)]">
            {{ $t('property.estateForm.sections.supply') }} {{ index + 1 }}
          </span>
          <!-- El primer bloque es el inmueble en sí y no se quita -->
          <button v-if="index > 0" type="button" :data-testid="`remove-supply-block-${index + 1}`" @click="removeSupplyBlock(index)"
            class="inline-flex items-center gap-1 text-sm text-[#385660] hover:text-red-500 cursor-pointer transition-colors">
            <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
              <path d="M5 5l10 10M15 5L5 15" />
            </svg>
            {{ $t('property.estateForm.buttons.removeSupply') }}
          </button>
        </div>

        <h2 class="text-[var(--c-primary)] font-semibold">1. {{ $t('property.estateForm.sections.estate') }}</h2>
        <div class="grid lg:grid-cols-4 md:grid-cols-2 grid-cols-1 gap-6">
          <inputPrimary v-model="block.address" :label="$t('property.estateForm.fields.address')"
            :placeholder="$t('property.estateForm.fields.addressPlaceholder')" />
          <inputPrimary v-model="block.postal_code" :label="$t('property.estateForm.fields.postalCode')"
            :placeholder="$t('property.estateForm.fields.postalCodePlaceholder')" :error="blockError(index, 'postal_code')" />
          <inputPrimary v-model="block.municipality" :label="$t('property.estateForm.fields.municipality')"
            :placeholder="$t('property.estateForm.fields.municipalityPlaceholder')" />
          <!-- La etiqueta del select se separa con padding (el reset global de base.css anula los márgenes) para igualarla al input -->
          <div class="pt-2 [&_label]:pb-2 [&_select]:w-full [&_select]:max-w-none">
            <SelectPrimary v-model="block.province" :name="`province-${index + 1}`" :options="provinces"
              :label="$t('property.estateForm.fields.province')" :placeholder="$t('property.estateForm.fields.provincePlaceholder')" />
          </div>
        </div>

        <h2 class="text-[var(--c-primary)] font-semibold pt-2">{{ $t('property.estateForm.sections.supplyData') }}</h2>
        <div v-if="!isEdit" class="grid lg:grid-cols-2 grid-cols-1 gap-6">
          <inputPrimary v-model="block.cups_light" :label="$t('property.estateForm.fields.cupsLight')"
            :placeholder="$t('property.estateForm.fields.cupsLightPlaceholder')" :error="blockError(index, 'cups_light')" />
          <inputPrimary v-model="block.cups_gas" :label="$t('property.estateForm.fields.cupsGas')"
            :placeholder="$t('property.estateForm.fields.cupsGasPlaceholder')" :error="blockError(index, 'cups_gas')" />
        </div>
        <!-- Tarifa luz / Tarifa gas, cada una con su catálogo; P1-P6 solo con luz -->
        <div class="grid lg:grid-cols-2 grid-cols-1 gap-6">
          <div class="[&_label]:pb-2 [&_select]:w-full [&_select]:max-w-none">
            <SelectPrimary v-model="block.light_rate" :name="`light_rate-${index + 1}`" :options="lightRateOptions(block.light_rate)" clearable
              :label="$t('property.estateForm.fields.lightRate')" :placeholder="$t('property.estateForm.fields.lightRatePlaceholder')"
              :error="blockError(index, 'light_rate')" />
          </div>
          <div class="[&_label]:pb-2 [&_select]:w-full [&_select]:max-w-none">
            <SelectPrimary v-model="block.gas_rate" :name="`gas_rate-${index + 1}`" :options="gasRateOptions(block.gas_rate)" clearable
              :label="$t('property.estateForm.fields.gasRate')" :placeholder="$t('property.estateForm.fields.gasRatePlaceholder')"
              :error="blockError(index, 'gas_rate')" />
          </div>
        </div>
        <div>
          <p class="text-[#385660] mb-2" :class="{ 'text-gray-400': !powersEnabled(block.light_rate) }">{{ $t('property.estateForm.fields.powers') }}</p>
          <div class="grid grid-cols-3 md:grid-cols-6 gap-4" :class="{ 'opacity-50': !powersEnabled(block.light_rate) }" data-testid="powers">
            <div v-for="period in POWER_PERIODS" :key="period">
              <label class="block text-xs mb-1" :class="powersEnabled(block.light_rate) ? 'text-[#385660]' : 'text-gray-400'" :for="`power-${index + 1}-${period}`">
                {{ period.toUpperCase() }}
              </label>
              <input :id="`power-${index + 1}-${period}`" v-model="block[period]" type="text" inputmode="decimal" placeholder="0"
                :disabled="!powersEnabled(block.light_rate)" :aria-disabled="!powersEnabled(block.light_rate)"
                class="w-full h-10 px-3 rounded-lg border border-[#3A5762] text-[#7CADB9] outline-none shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)] disabled:bg-gray-100 disabled:text-gray-400 disabled:border-gray-300 disabled:cursor-not-allowed"
                :class="{ 'border-red-500': blockError(index, period) }" />
              <span v-if="blockError(index, period)" class="text-red-500 text-xs">{{ blockError(index, period) }}</span>
            </div>
          </div>
          <p class="text-xs text-gray-500 pt-2">
            {{ powersEnabled(block.light_rate) ? $t('property.estateForm.fields.powersHint') : (block.gas_rate ? $t('property.estateForm.fields.powersDisabled') : $t('property.estateForm.fields.powersHint')) }}
          </p>
        </div>
      </section>

      <!-- Añadir otro suministro: hasta tres en el alta -->
      <div v-if="!isEdit" class="flex flex-col sm:flex-row sm:items-center gap-3">
        <button v-if="canAddSupplyBlock(supplyBlocks)" type="button" data-testid="add-supply-block" @click="addSupplyBlock"
          class="inline-flex items-center gap-3 py-2 px-5 rounded-lg border border-[#25B0D7] bg-white text-[#25B0D7] font-semibold uppercase shadow-[0px_4px_4px_0px_rgba(0,0,0,0.15)] cursor-pointer transition-colors hover:bg-[#25B0D7]/10">
          <PlusIcon class="w-5 h-5 shrink-0" />
          {{ $t('property.estateForm.buttons.addSupply') }}
        </button>
        <p class="text-sm text-[#385660]">{{ $t('property.estateForm.hints.maxSupplies') }}</p>
      </div>

      <!-- 2. Datos del titular -->
      <section class="flex flex-col gap-4 p-6 rounded-xl border border-gray-200 bg-white shadow-[0px_4px_4px_0px_rgba(0,0,0,0.15)]">
        <h2 class="text-[var(--c-primary)] font-semibold">2. {{ $t('property.estateForm.sections.owner') }}</h2>
        <div class="grid lg:grid-cols-5 md:grid-cols-2 grid-cols-1 gap-6">
          <inputPrimary v-model="form.owner_name" :label="$t('property.estateForm.fields.ownerName')"
            :placeholder="$t('property.estateForm.fields.ownerNamePlaceholder')" />
          <inputPrimary v-model="form.cif" :label="$t('property.estateForm.fields.ownerDocument')"
            :placeholder="$t('property.estateForm.fields.ownerDocumentPlaceholder')" :error="validationErrors.cif" />
          <inputPrimary v-model="form.owner_email" type="email" :label="$t('property.estateForm.fields.ownerEmail')"
            :placeholder="$t('property.estateForm.fields.ownerEmailPlaceholder')" :error="validationErrors.owner_email" />
          <inputPrimary v-model="form.telephone" :label="$t('property.estateForm.fields.ownerPhone')"
            :placeholder="$t('property.estateForm.fields.ownerPhonePlaceholder')" :error="validationErrors.telephone" />
          <inputPrimary v-model="form.iban" :label="$t('property.estateForm.fields.iban')"
            :placeholder="$t('property.estateForm.fields.ibanPlaceholder')" :error="validationErrors.iban" />
        </div>
      </section>

      <!-- 3. Información adicional -->
      <section class="flex flex-col gap-4 p-6 rounded-xl border border-gray-200 bg-white shadow-[0px_4px_4px_0px_rgba(0,0,0,0.15)]">
        <h2 class="text-[var(--c-primary)] font-semibold">3. {{ $t('property.estateForm.sections.additional') }}</h2>
        <div class="grid lg:grid-cols-2 grid-cols-1 gap-6">
          <textareaPrimary v-model="form.comments" :rows="3" :label="$t('property.estateForm.fields.comments')"
            :placeholder="$t('property.estateForm.fields.commentsPlaceholder')" />
          <textareaPrimary v-model="form.other" :rows="3" :label="$t('property.estateForm.fields.other')"
            :placeholder="$t('property.estateForm.fields.otherPlaceholder')" />
        </div>
      </section>

      <!-- 4. Documentación -->
      <section class="flex flex-col gap-4 p-6 rounded-xl border border-gray-200 bg-white shadow-[0px_4px_4px_0px_rgba(0,0,0,0.15)]">
        <h2 class="text-[var(--c-primary)] font-semibold">4. {{ $t('property.estateForm.sections.documents') }}</h2>
        <div class="grid lg:grid-cols-3 md:grid-cols-2 grid-cols-1 gap-6">
          <div v-for="slot in FILE_SLOTS" :key="slot">
            <inputPrimary type="file" class="w-full" accept=".jpg,.jpeg,.png,.pdf"
              :label="$t(`property.estateForm.files.${slot}`)"
              :placeholder="files[slot]?.file_name || $t('property.estateForm.files.choose')"
              :error="validationErrors[slot]"
              @change="handleFileChange($event, slot)" />
            <span v-if="files[slot]?.name || files[slot]?.file_name" class="text-sm text-gray-600">
              {{ files[slot].name || files[slot].file_name }}
            </span>
          </div>
        </div>
        <p class="text-xs text-gray-500 pt-2">{{ $t('property.estateForm.files.hint') }}</p>
      </section>

      <p v-if="error" class="text-red-500 text-sm">{{ error }}</p>

      <div class="flex flex-col sm:flex-row justify-end gap-3 pt-2">
        <ButtonWhite class="sm:w-40 w-full" @click="cancel" :disabled="submitting">
          {{ $t('property.estateForm.buttons.cancel') }}
        </ButtonWhite>
        <ButtonWhite class="sm:w-40 w-full" bgColor="bg-[#25B0D7]" textColor="text-white" hoverBgColor="bg-[#2AB4D9]"
          :disabled="submitting" :class="{ 'opacity-50 cursor-not-allowed': submitting }" @click="submitForm">
          {{ isEdit ? $t('property.form.buttons.saveChanges') : $t('property.form.buttons.save') }}
        </ButtonWhite>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted, nextTick } from 'vue';
import axios from 'axios';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import titlePrimary from '@/components/title/title-primary.vue';
import ButtonWhite from '@/components/button/ButtonWhite.vue';
import inputPrimary from '@/components/inputs/input-primary.vue';
import textareaPrimary from '@/components/inputs/textarea-primary.vue';
import SelectPrimary from '@/components/select/SelectPrimary.vue';
import { isAdsUser as checkIsAdsUser } from '@/services/userRoles';
import { accountTypeLabel } from '@/services/clientAccount';
import PlusIcon from '@/assets/svg/PlusIcon.vue';
import { provinceOptions } from '@/constants/provinces';
import { POWER_PERIODS } from '@/constants/supplyRates';
import { lightRateOptions, gasRateOptions, powersEnabled, ratesPayload } from '@/services/estateSupplyRates';
import { emptySupplyBlock, supplyBlockFromEstate, canAddSupplyBlock, validateSupplyBlocks, supplyBlocksPayload, LOCATION_FIELDS } from '@/services/estateSupplyBlocks';

/**
 * Ficha de inmueble de la cartera del cliente vertical (INM-01/INM-02), con las
 * cuatro secciones de la propuesta visual de ADS (10-09-2026) y, desde el
 * 16/09/2026, hasta tres bloques "Suministro" en el alta: cada uno con los
 * datos del inmueble y del suministro (CUPS, tarifas y potencias). Todo es
 * opcional: la ficha se guarda incompleta y se completa después. El cliente no
 * elige cuenta: el back la toma de su acceso. ADS sí la elige.
 */
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const id = route.params.id || null;
const isEdit = computed(() => !!id);
const isAdsUser = checkIsAdsUser();
const provinces = provinceOptions();

/**
 * Cuentas del propio cliente. Quien pertenece a una sola no ve nada: el back
 * la deduce de su acceso. Quien pertenece a varias tiene que decir en cuál va
 * el inmueble, porque el sistema no puede elegir por él.
 */
const ownAccounts = ref([]);
// Un inmueble solo se da de alta en una cuenta vertical: una persona con una
// cuenta vertical y otra horizontal (Belvis e INMOBELVA) no debe poder elegir
// la horizontal. Una cuenta sin clasificar no se descarta.
const selectableAccounts = computed(() => ownAccounts.value.filter((account) => {
  const types = Array.isArray(account.types) ? account.types : [];
  return types.length === 0 || types.includes('vertical');
}));
const ownAccountOptions = computed(() => selectableAccounts.value.map((account) => ({
  value: account.id,
  label: account.type_label ? `${account.name} · ${account.type_label}` : account.name,
})));

const loadOwnAccounts = async () => {
  if (isAdsUser) return;
  try {
    const response = await axios.get(`${import.meta.env.VITE_API_URL}/client-account/mine`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    });
    ownAccounts.value = Array.isArray(response.data?.value) ? response.data.value : [];
  } catch {
    ownAccounts.value = [];
  }
  // Si tras acotar queda una sola cuenta vertical, va esa: el back no puede
  // deducirla cuando la persona pertenece a varias.
  if (selectableAccounts.value.length === 1) {
    selectedAccount.value = selectableAccounts.value[0];
  }
};

const selectOwnAccount = (value) => {
  selectedAccount.value = ownAccounts.value.find((account) => String(account.id) === String(value)) || null;
  validationErrors.value.propietario_id = '';
};

// Tipos de documento que entiende el back (PropertyFile::FILE_TYPES)
const FILE_SLOTS = ['owner_document', 'property_document', 'other'];
const MAX_FILE_MB = 5;

const form = reactive({
  owner_name: '', cif: '', owner_email: '', telephone: '', iban: '',
  comments: '', other: '',
});

// --- Bloques de suministro --------------------------------------------------
// El alta arranca con "Suministro 1"; los bloques 2 y 3 se abren a demanda. En
// edición hay un solo bloque con la ubicación de la finca y las tarifas de sus
// suministros (los CUPS se gestionan desde la ficha).
let blockUid = 0;
const newBlock = (location = {}) => ({ uid: blockUid += 1, ...emptySupplyBlock(location) });
const supplyBlocks = reactive([newBlock()]);
const blockError = (index, field) => validationErrors.value[`supplies.${index}.${field}`] || '';
// Los errores de bloque van por índice: al añadir o quitar uno se recalculan al guardar.
const clearBlockErrors = () => {
  Object.keys(validationErrors.value).filter((key) => key.startsWith('supplies.')).forEach((key) => { delete validationErrors.value[key]; });
};
const addSupplyBlock = () => {
  if (!canAddSupplyBlock(supplyBlocks)) return;
  // Precarga la ubicación del bloque anterior: suele ser el mismo inmueble
  supplyBlocks.push(newBlock(supplyBlocks[supplyBlocks.length - 1]));
  clearBlockErrors();
  if (typeof document !== 'undefined') {
    nextTick(() => document.getElementById(`supply-block-${supplyBlocks.length}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  }
};
const removeSupplyBlock = (index) => {
  if (index === 0 || index >= supplyBlocks.length) return;
  supplyBlocks.splice(index, 1);
  clearBlockErrors();
};
// Sin tarifa de luz no hay potencias: se limpian en el bloque que la pierde.
watch(() => supplyBlocks.map((block) => block.light_rate), (rates) => {
  rates.forEach((rate, index) => { if (!rate) POWER_PERIODS.forEach((period) => { supplyBlocks[index][period] = ''; }); });
});
const files = reactive({ owner_document: null, property_document: null, other: null });
const validationErrors = ref({});
const error = ref('');
const loading = ref(false);
const submitting = ref(false);

// --- Cuenta cliente (solo ADS) -------------------------------------------
const accountSearch = ref('');
const accountResults = ref([]);
const selectedAccount = ref(null);
let accountTimeout = null;
let skipNextSearch = false;

watch(accountSearch, (query) => {
  if (skipNextSearch) { skipNextSearch = false; return; }
  if (accountTimeout) clearTimeout(accountTimeout);
  if (!query || query.trim().length < 2) { accountResults.value = []; return; }
  accountTimeout = setTimeout(async () => {
    try {
      const response = await axios.get(`${import.meta.env.VITE_API_URL}/client-account/search`, {
        params: { query: query.trim(), per_page: 10 },
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      accountResults.value = response.data?.value?.data || [];
    } catch {
      accountResults.value = [];
    }
  }, 300);
});

const selectAccount = (account) => {
  selectedAccount.value = account;
  skipNextSearch = true;
  accountSearch.value = account.name;
  accountResults.value = [];
  validationErrors.value.propietario_id = '';
};

// --- Archivos -------------------------------------------------------------
const handleFileChange = (event, slot) => {
  const file = event.target.files?.[0];
  if (!file) return;
  validationErrors.value[slot] = '';
  const okType = ['image/jpeg', 'image/png', 'application/pdf'].includes(file.type);
  if (!okType) { validationErrors.value[slot] = t('property.estateForm.validation.fileType'); return; }
  if (file.size > MAX_FILE_MB * 1024 * 1024) { validationErrors.value[slot] = t('property.estateForm.validation.fileSize'); return; }
  files[slot] = file;
};

const uploadFiles = async (propertyId) => {
  for (const slot of FILE_SLOTS) {
    const file = files[slot];
    if (!(file instanceof File)) continue;
    const payload = new FormData();
    payload.append('file', file);
    payload.append('file_type', slot);
    await axios.post(`${import.meta.env.VITE_API_URL}/property/upload/${propertyId}`, payload, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'multipart/form-data', Accept: 'application/json' },
    });
  }
};

// --- Validación mínima: solo formato de lo que se haya rellenado ---------
const validate = () => {
  validationErrors.value = {};
  if (form.owner_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.owner_email)) {
    validationErrors.value.owner_email = t('property.estateForm.validation.email');
  }
  if (form.iban && !/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(form.iban.replace(/\s+/g, '').toUpperCase())) {
    validationErrors.value.iban = t('property.estateForm.validation.iban');
  }
  if (!isEdit.value && !selectedAccount.value && (isAdsUser || selectableAccounts.value.length > 1)) {
    validationErrors.value.propietario_id = t('property.estateForm.validation.account');
  }
  Object.entries(validateSupplyBlocks(supplyBlocks)).forEach(([field, key]) => { validationErrors.value[field] = t(key); });
  return Object.keys(validationErrors.value).length === 0;
};

const clean = (value) => {
  const trimmed = typeof value === 'string' ? value.trim() : value;
  return trimmed === '' ? null : trimmed;
};

const submitForm = async () => {
  error.value = '';
  if (!validate()) { error.value = t('property.estateForm.validation.review'); return; }
  submitting.value = true;
  try {
    // La finca lleva la ubicación del primer bloque. En el alta los suministros
    // viajan en `supplies[]`; en edición las tarifas van planas, como hasta ahora.
    const [firstBlock] = supplyBlocks;
    const payload = {
      type: 'inmueble',
      ...Object.fromEntries(LOCATION_FIELDS.map((field) => [field, clean(firstBlock[field])])),
      owner_name: clean(form.owner_name),
      cif: clean(form.cif),
      owner_email: clean(form.owner_email),
      telephone: clean(form.telephone),
      iban: form.iban ? form.iban.replace(/\s+/g, '').toUpperCase() : null,
      comments: clean(form.comments),
      other: clean(form.other),
    };
    if (isEdit.value) {
      Object.assign(payload, ratesPayload(firstBlock));
    } else {
      const supplies = supplyBlocksPayload(supplyBlocks);
      if (supplies.length) payload.supplies = supplies;
    }
    if (selectedAccount.value) payload.propietario_id = selectedAccount.value.id;

    const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
    let propertyId = id;
    if (isEdit.value) {
      await axios.put(`${import.meta.env.VITE_API_URL}/property/update/${id}`, payload, { headers });
    } else {
      const response = await axios.post(`${import.meta.env.VITE_API_URL}/property/create`, payload, { headers });
      propertyId = response.data.value.id;
    }
    await uploadFiles(propertyId);
    await router.push(`/Property-detail/${propertyId}`);
  } catch (err) {
    if (err.response?.status === 422 && err.response.data?.errors) {
      Object.entries(err.response.data.errors).forEach(([field, messages]) => { validationErrors.value[field] = messages[0]; });
      error.value = t('property.estateForm.validation.review');
    } else {
      error.value = err.response?.data?.value || t('property.estateForm.validation.saveError');
    }
  } finally {
    submitting.value = false;
  }
};

const cancel = () => router.back();

// --- Edición ----------------------------------------------------------------
const fetchEstate = async () => {
  loading.value = true;
  try {
    const response = await axios.get(`${import.meta.env.VITE_API_URL}/property/get/${id}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    });
    const data = response.data?.value || {};
    Object.keys(form).forEach((key) => { form[key] = data[key] ?? ''; });
    supplyBlocks.splice(0, supplyBlocks.length, { uid: blockUid += 1, ...supplyBlockFromEstate(data) });
    (data.files || []).forEach((file) => {
      const slot = FILE_SLOTS.includes(file.file_type) ? file.file_type : null;
      if (slot && !files[slot]) files[slot] = file;
    });
  } catch {
    error.value = t('property.estateForm.validation.loadError');
  } finally {
    loading.value = false;
  }
};

onMounted(() => { if (isEdit.value) fetchEstate(); });
onMounted(loadOwnAccounts);
</script>
